import React from 'react';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { SizeMap, VariationMap } from '@gokrew/models/ui.types';
import type { ButtonProps, IconButtonProps } from '@gokrew/models/button';
import { CountBadge } from '@/components/CountBadge';
import styles from './Button.module.css';

export function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  readOnly = false,
  children,
  className = '',
  IconSuffix = null,
  IconPrefix = null,
  badgeCount,
  onClick,
  ...props
}: ButtonProps) {
  const variants: VariationMap<string> = {
    primary: styles.btnPrimary,
    secondary: styles.btnSecondary,
    outline: styles.btnOutline,
    solidPrimary: styles.btnSolidPrimary,
    leavesPrimary: styles.btnLeavesPrimary,
    white: styles.btnWhite,
    whiteOutline: styles.btnWhiteOutline,
    dangerOutline: styles.btnDangerOutline,
    ghost: styles.btnGhost,
    ghostBorder: styles.btnGhostBorder,
    adminPrimary: styles.btnAdminPrimary,
    destructive: styles.btnDestructive,
    danger: styles.btnDanger,
    success: styles.btnSuccess,
  };

  const sizes: SizeMap<string> = {
    xs: styles.btnXs,
    sm: styles.btnSm,
    md: styles.btnMd,
    lg: styles.btnLg,
  };

  const iconSizes: SizeMap<number> = {
    xs: 14,
    sm: 16,
    md: 20,
    lg: 24,
  };

  return (
    <button
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      className={cn(
        styles.btnBase,
        sizes[size],
        variants[variant],
        disabled && styles.btnDisabled,
        readOnly && 'pointer-events-none',
        badgeCount !== undefined && styles.btnBadgeAnchor,
        className
      )}
      {...props}
    >
      {IconPrefix && <IconPrefix size={iconSizes[size]} />}
      {children}
      {IconSuffix && <IconSuffix size={iconSizes[size]} />}
      {badgeCount !== undefined && (
        <CountBadge count={badgeCount} className={styles.btnBadge} />
      )}
    </button>
  );
}

export function IconButton({
  Icon = null,
  size = 'md',
  className = '',
  disabled = false,
  onClick,
  ...props
}: React.ComponentProps<LucideIcon> & IconButtonProps) {
  if (!Icon) return null;

  const iconSizes: SizeMap<number> = {
    xs: 14,
    sm: 16,
    md: 20,
    lg: 24,
  };

  const roundedClass = `rounded-${size || 'lg'}`;

  const paddingClass: SizeMap<string> = {
    xs: 'p-1',
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  };

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={!disabled ? onClick : undefined}
      className={cn(
        styles.iconBtnBase,
        roundedClass,
        paddingClass[size],
        disabled && styles.iconBtnDisabled,
        className
      )}
    >
      <Icon {...props} size={iconSizes[size]} />
    </button>
  );
}
